import * as React from "react"
import { Link, useNavigate } from "react-router-dom"
import { Search, Menu, X } from "lucide-react"

import { Button } from "./ui/button"
import { Input } from "./ui/input"
import Site from '../data/siteConfig.json';

interface HeaderProps {
  onSearch?: (query: string) => void
}

const Header: React.FC<HeaderProps> = ({ onSearch }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const [searchQuery, setSearchQuery] = React.useState("")
  const navigate = useNavigate()

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const query = searchQuery.trim()
    if (!query) return

    navigate(`/templates?search=${encodeURIComponent(query)}`)
    onSearch?.(query)
  }

  const navLinks = React.useMemo(
    () => [
      { label: "Home", path: "/" },
      { label: "Templates", path: "/templates" },
      { label: "Courses", path: "/courses" },
      { label: "Blog", path: "/blog" },
      { label: "Services", path: "/services" },
      { label: "YouTube", path: "https://youtube.com/websitedevelopers" },
    ],
    []
  )

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg">
              <img src={Site.logo} alt={Site.name} className="w-full" loading="lazy"/>
            </div>
            <span className="text-xl font-bold text-gray-900 text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-blue-600">
              {Site.name}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="rounded-lg px-4 py-2 text-gray-700 transition-all duration-200 hover:bg-sky-50 hover:text-sky-500"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Search & CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <form onSubmit={handleSearch} className="relative">
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48 bg-gray-50 pl-10 pr-4 transition-all focus:bg-white lg:w-64"
              />
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </form>

            <Link to="/hire">
              <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-md transition-all hover:from-sky-600 hover:to-blue-700 hover:shadow-lg">
                Hire us
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            type="button"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            className="rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-100 lg:hidden"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="border-t border-gray-200 py-4 lg:hidden">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className="rounded-lg px-4 py-2 text-gray-700 transition-colors hover:bg-sky-50 hover:text-sky-500"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            <form onSubmit={handleSearch} className="relative mt-4">
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-gray-50 pl-10 pr-4"
              />
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            </form>

            <Link to="/hire" className="mt-4 block">
              <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white">
                Get Started
              </Button>
            </Link>
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
